import { useQuery } from 'react-query';

import type {
  BomStructure,
  BomBuilderStructureState,
  BomPartFile,
} from '@frontend/api';
import { useRequest, type QueryFunction } from '@frontend/api/src/common';

/**
 * Some drafts have the part map stored with the keys missing dashes
 * This function transforms the part map to have UUIDs as keys
 * @deprecated This will not be necessary once older drafts have been phased out.
 * @param data The draft state from the BOM Builder API
 * @returns The draft state with the part map transformed to a map with UUIDs as keys
 */
const backwardsCompatibleDraftStateTransform = (data: BomStructure | null) => {
  if (!data) {
    return null;
  }

  const partMap = Object.fromEntries(
    Object.entries(data.partMap).map(([, part]) => [part.uuid, part]),
  );

  return {
    ...data,
    partMap,
  };
};

export type BomBuilderDetailsResponse = Omit<
  BomBuilderStructureState,
  'partMap' | 'nodeMap'
> & {
  structure: BomStructure;
  draftState: BomBuilderStructureState | null;
  draftUpdatedDate: string | null;
  committedAssemblyParentFiles: BomPartFile[];
};

type UseGetBOMBuilderDetailsParams = {
  quoteItemId: number;
};

export type GetBOMBuilderDetailsQueryKeyType = readonly [
  'bomBuilderDetails',
  number,
];

export const getBOMBuilderDetailsQueryKey = (
  params: UseGetBOMBuilderDetailsParams,
): GetBOMBuilderDetailsQueryKeyType => [
  'bomBuilderDetails',
  params.quoteItemId,
];

export const useGetBom: QueryFunction<
  BomBuilderDetailsResponse,
  [UseGetBOMBuilderDetailsParams],
  GetBOMBuilderDetailsQueryKeyType
> = (params, { requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  const queryKey = getBOMBuilderDetailsQueryKey(params);

  return useQuery({
    ...queryOptions,
    queryKey,
    queryFn: ({ signal }) => {
      return request<BomBuilderDetailsResponse>(
        `/quotes/items/${params.quoteItemId}/bom_builder/`,
        {
          init: {
            method: 'GET',
            signal,
          },
        },
      ).then(
        (response) =>
          ({
            ...response,
            draftState: backwardsCompatibleDraftStateTransform(
              response.draftState,
            ),
          } as BomBuilderDetailsResponse),
      );
    },
  });
};
